import { createBrowserRouter, Navigate, Outlet } from 'react-router';

import { MainLayout } from '@/components';
import { MainAboutBankPage } from '@/pages/(publicPages)/(main)/MainAboutBankPage/MainAboutBankPage';
import { MainAboutSchoolPage } from '@/pages/(publicPages)/(main)/MainAboutSchoolPage/MainAboutSchoolPage';
import { MainAdvantagesPage } from '@/pages/(publicPages)/(main)/MainAdvantagesPage/MainAdvantagesPage';
import { MainCatalogPage } from '@/pages/(publicPages)/(main)/MainCatalogPage/MainCatalogPage';
import { MainContactPage } from '@/pages/(publicPages)/(main)/MainContactPage/MainContactPage';
import { MainEditPage } from '@/pages/(publicPages)/(main)/MainEditPage/MainEditPage';
import { MainFAQsPage } from '@/pages/(publicPages)/(main)/MainFAQsPage/MainFAQsPage';
import { MainReviewsPage } from '@/pages/(publicPages)/(main)/MainReviewsPage/MainReviewsPage';
import { SchoolCreatePage } from '@/pages/(publicPages)/(schools)/SchoolCreatePage/SchoolCreatePage';
import { SchoolEditPage } from '@/pages/(publicPages)/(schools)/SchoolEditPage/SchoolEditPage';
import { SchoolsCatalogPage } from '@/pages/(publicPages)/(schools)/SchoolsCatalogPage/SchoolsCatalogPage';
import { SchoolSubsectionPage } from '@/pages/(publicPages)/(schools)/SchoolSubsectionPage/SchoolSubsectionPage';
import { SchoolViewPage } from '@/pages/(publicPages)/(schools)/SchoolViewPage/SchoolViewPage';
import { HomePage } from '@/pages/HomePage';
import { FeatureFlagGuard, RoleCheckGuard, SchoolTypeGuard } from '@/shared/components';
import { EFeatureFlags, EUserRole, ROUTES } from '@/shared/consts';
import { ESchoolSubsection, ESchoolType } from '@/shared/types';

import { SurveyCreatePage } from './pages/(publicPages)/(schools)/(schoolSelectionPanel)/AdmissionSurveyTab/(pages)/SurveyCreatePage/SurveyCreatePage';
import { SurveyEditPageWrapper } from './pages/(publicPages)/(schools)/(schoolSelectionPanel)/AdmissionSurveyTab/(pages)/SurveyEditPage/SurveyEditPageWrapper';
import { SurveyViewPage } from './pages/(publicPages)/(schools)/(schoolSelectionPanel)/AdmissionSurveyTab/(pages)/SurveyViewPage/SurveyViewPage';
import { ApplicationsPageWrapper } from './pages/(publicPages)/(schools)/(schoolSelectionPanel)/ApplicationsTab/(pages)/ApplicationsPage/ApplicationsPageWrapper';
import { CurrentApplicationPageWrapper } from './pages/(publicPages)/(schools)/(schoolSelectionPanel)/ApplicationsTab/(pages)/CurrentApplicationPage/CurrentApplicationPageWrapper';
import { EnrolledListPageWrapper } from './pages/(publicPages)/(schools)/(schoolSelectionPanel)/EnrolledTab/(pages)/EnrolledListPage/EnrolledListPageWrapper';
import { TestingParticipantsPageWrapper } from './pages/(publicPages)/(schools)/(schoolSelectionPanel)/TestingTab/(pages)/TestingParticipantsPage/TestingParticipantsPageWrapper';
import { UsersAssignmentPageWrapper } from './pages/(publicPages)/(schools)/(schoolSelectionPanel)/TestingTab/(pages)/UsersAssignmentPage/UsersAssignmentPageWrapper';
import { UsersPage } from './pages/(publicPages)/(schools)/(schoolSelectionPanel)/UsersTab/(pages)/UsersPage/UsersPage';

const {
  SCHOOLS,
  HOME,
  CREATE,
  EDIT,
  VIEW,
  SCHOOL_FAQ,
  SCHOOL_PROGRAM,
  SCHOOL_ABILITIES,
  SCHOOL_SPEAKERS,
  PUBLIC_PAGES,
  MAIN,
  MAIN_ABOUT_SCHOOL,
  MAIN_ADVANTAGES,
  MAIN_REVIEWS,
  MAIN_ABOUT_BANK,
  MAIN_FAQS,
  MAIN_CONTACT,
  SELECTION_PANEL,
  ADMISSION_SURVEY,
  APPLICATIONS,
  TESTING,
  ENROLLED,
  USERS,
  USERS_ASSIGNMENT,
} = ROUTES;

export const router = createBrowserRouter([
  {
    element: <MainLayout />,
    children: [
      {
        path: HOME,
        element: <HomePage />,
      },
      {
        path: PUBLIC_PAGES,
        element: <Outlet />,
        children: [
          {
            index: true,
            element: <Navigate to={MAIN} />,
          },
          {
            path: MAIN,
            element: (
              <RoleCheckGuard permittedRoles={[EUserRole.Admin]}>
                <Outlet />
              </RoleCheckGuard>
            ),
            children: [
              {
                index: true,
                element: <MainCatalogPage />,
              },
              {
                path: ':id',
                element: <Outlet />,
                children: [
                  {
                    index: true,
                    element: <MainEditPage />,
                  },
                  { path: MAIN_ABOUT_SCHOOL, element: <MainAboutSchoolPage /> },
                  { path: MAIN_ADVANTAGES, element: <MainAdvantagesPage /> },
                  { path: MAIN_REVIEWS, element: <MainReviewsPage /> },
                  { path: MAIN_ABOUT_BANK, element: <MainAboutBankPage /> },
                  { path: MAIN_FAQS, element: <MainFAQsPage /> },
                  { path: MAIN_CONTACT, element: <MainContactPage /> },
                ],
              },
            ],
          },
          {
            path: SCHOOLS,
            element: (
              <RoleCheckGuard permittedRoles={[EUserRole.Admin, EUserRole.Curator]}>
                <Outlet />
              </RoleCheckGuard>
            ),
            children: [
              {
                index: true,
                element: <SchoolsCatalogPage />,
              },
              {
                path: CREATE,
                element: <SchoolCreatePage />,
              },
              {
                path: ':id',
                element: <Outlet />,
                children: [
                  {
                    index: true,
                    element: (
                      <Navigate
                        to={EDIT}
                        replace
                      />
                    ),
                  },
                  {
                    path: SELECTION_PANEL,
                    element: (
                      <SchoolTypeGuard permittedTypes={[ESchoolType.Public]}>
                        <Outlet />
                      </SchoolTypeGuard>
                    ),
                    children: [
                      {
                        index: true,
                        element: (
                          <Navigate
                            to={ADMISSION_SURVEY}
                            replace
                          />
                        ),
                      },
                      {
                        path: ADMISSION_SURVEY,
                        element: <Outlet />,
                        children: [
                          {
                            index: true,
                            element: (
                              <Navigate
                                to={VIEW}
                                replace
                              />
                            ),
                          },
                          {
                            path: CREATE,
                            element: <SurveyCreatePage />,
                          },
                          {
                            path: EDIT,
                            element: <SurveyEditPageWrapper />,
                          },
                          {
                            path: VIEW,
                            element: <SurveyViewPage />,
                          },
                        ],
                      },
                      {
                        path: APPLICATIONS,
                        element: <Outlet />,
                        children: [
                          {
                            index: true,
                            element: <ApplicationsPageWrapper />,
                          },
                          {
                            path: ':applicationId',
                            element: <CurrentApplicationPageWrapper />,
                          },
                        ],
                      },
                      {
                        path: TESTING,
                        element: <Outlet />,
                        children: [
                          {
                            index: true,
                            element: <TestingParticipantsPageWrapper />,
                          },
                          {
                            path: USERS_ASSIGNMENT,
                            element: (
                              <FeatureFlagGuard flag={EFeatureFlags.AdmissionExamReserve}>
                                <UsersAssignmentPageWrapper />
                              </FeatureFlagGuard>
                            ),
                          },
                        ],
                      },
                      {
                        path: USERS,
                        element: <Outlet />,
                        children: [
                          {
                            index: true,
                            element: <UsersPage />,
                          },
                        ],
                      },
                      {
                        path: ENROLLED,
                        element: (
                          <FeatureFlagGuard flag={EFeatureFlags.EnrolledSectionParticipants}>
                            <EnrolledListPageWrapper />
                          </FeatureFlagGuard>
                        ),
                      },
                    ],
                  },
                  {
                    path: EDIT,
                    element: <Outlet />,
                    children: [
                      {
                        index: true,
                        element: <SchoolEditPage />,
                      },
                      {
                        path: SCHOOL_FAQ,
                        element: <SchoolSubsectionPage type={ESchoolSubsection.Faq} />,
                      },
                      {
                        path: SCHOOL_PROGRAM,
                        element: <SchoolSubsectionPage type={ESchoolSubsection.Program} />,
                      },
                      {
                        path: SCHOOL_ABILITIES,
                        element: <SchoolSubsectionPage type={ESchoolSubsection.Abilities} />,
                      },
                      {
                        path: SCHOOL_SPEAKERS,
                        element: <SchoolSubsectionPage type={ESchoolSubsection.Speakers} />,
                      },
                    ],
                  },
                  {
                    path: VIEW,
                    element: <SchoolViewPage />,
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
]);
